import { useAccount, useMsal } from '@azure/msal-react';

import { fetchMeCountries, fetchUser } from 'api/user';
import { authenticateClients, config } from 'application';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { ICountry, IUser } from 'types/dto';

interface IContextSession {
  user: IUser | null;
  allowedCountries: ICountry[];
  isLoading: boolean;
  isAuthenticated: boolean;
}

type SessionContextValue =
  | [IContextSession | undefined, React.Dispatch<React.SetStateAction<IContextSession>>]
  | undefined;

export const defaultSessionValue: IContextSession = {
  user: null,
  allowedCountries: [],
  isLoading: false,
  isAuthenticated: false,
};

export const SessionContext = React.createContext<SessionContextValue>(undefined);

export const SessionProvider: React.FC<{ children: React.ReactElement }> = ({ children, ...props }) => {
  const [session, setSession] = useState(defaultSessionValue);
  const [memoSession, memoSetSession] = useMemo(() => [session, setSession], [session]);
  const { instance, accounts } = useMsal();
  const account = useAccount(accounts[0] || null);

  useEffect(() => {
    if (account && !session.user && !session.isLoading) {
      setSession({
        ...session,
        isLoading: true,
      });

      instance
        .acquireTokenSilent({
          scopes: config.msal.tokenScopes,
          account,
        })
        .then(authResult => {
          authenticateClients(authResult);
          return Promise.all([fetchUser(authResult.accessToken), fetchMeCountries()]);
        })
        .then(([user, allowedCountries]) => setSession({ ...session, user, allowedCountries, isLoading: false }))
        .catch(err => {
          setSession({ ...session, isLoading: false });
          sessionStorage.clear();
          window.history.pushState(null, '', '/');
          console.error(err);
        });
    }
  }, [account, instance, session]);

  return (
    <SessionContext.Provider value={[memoSession, memoSetSession]} {...props}>
      {children}
    </SessionContext.Provider>
  );
};

export const useSession = () => {
  const context = useContext(SessionContext);

  if (!context) {
    throw new Error('useSession must be used within a SessionProvider');
  }

  return context;
};
